import * as Yup from 'yup';
import { CustomerTypeOption, UsageLimitOption } from '../Types/booking-discounts.types';

const CODE_CHARACTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export const DISCOUNT_CODE_MIN_LENGTH = 4;
export const DISCOUNT_CODE_MAX_LENGTH = 20;

export const generateRandomDiscountCode = (): string => {
    let generatedCode = '';
    for (let charIndex = 0; charIndex < 8; charIndex += 1) {
        const randomIndex = Math.floor(Math.random() * CODE_CHARACTERS.length);
        generatedCode += CODE_CHARACTERS[randomIndex];
    }
    return generatedCode;
};

export const CUSTOMER_TYPE_OPTIONS: CustomerTypeOption[] = [
    { key: 'ALL', labelKey: 'BookingDiscounts.LimitAll', hintKey: 'BookingDiscounts.LimitAllHint' },
    { key: 'NEW', labelKey: 'BookingDiscounts.LimitNew', hintKey: 'BookingDiscounts.LimitNewHint' },
    { key: 'EXISTING', labelKey: 'BookingDiscounts.LimitExisting', hintKey: 'BookingDiscounts.LimitExistingHint' },
];

export const USAGE_LIMIT_OPTIONS: UsageLimitOption[] = [
    { key: 'unlimited', labelKey: 'BookingDiscounts.LimitNo', hintKey: 'BookingDiscounts.LimitNoHint' },
    { key: 'limited', labelKey: 'BookingDiscounts.LimitYes', hintKey: 'BookingDiscounts.LimitYesHint' },
];

export const bookingDiscountValidationSchema = Yup.object()
    .shape({
        code: Yup.string()
            .trim()
            .required('BookingDiscounts.ToastErrorCodeRequired')
            .min(DISCOUNT_CODE_MIN_LENGTH, 'BookingDiscounts.ToastErrorCodeMinLength')
            .max(DISCOUNT_CODE_MAX_LENGTH, 'BookingDiscounts.ToastErrorCodeMaxLength'),
        description: Yup.string().nullable().optional(),
        active: Yup.boolean().default(true),
        noExpiry: Yup.boolean().default(true),
        expiresAt: Yup.string()
            .nullable()
            .when('noExpiry', {
                is: false,
                then: (schemaItem: Yup.StringSchema<string | null | undefined>) =>
                    schemaItem.required('BookingDiscounts.ToastErrorExpiryRequired'),
                otherwise: (schemaItem: Yup.StringSchema<string | null | undefined>) => schemaItem.nullable().optional(),
            }),
        discountType: Yup.string().oneOf(['percentage', 'fixed']).required(),
        value: Yup.number()
            .typeError('BookingDiscounts.ToastErrorValueInvalid')
            .positive('BookingDiscounts.ToastErrorValueInvalid')
            .required('BookingDiscounts.ToastErrorValueInvalid')
            .when('discountType', {
                is: 'percentage',
                then: (schemaItem: Yup.NumberSchema) => schemaItem.max(100, 'BookingDiscounts.ToastErrorPercentMax'),
            }),
        allTreatments: Yup.boolean().default(true),
        serviceIds: Yup.array()
            .of(Yup.number().required())
            .when('allTreatments', {
                is: false,
                then: (schemaItem: Yup.ArraySchema<Yup.AnySchema>) =>
                    schemaItem.min(1, 'BookingDiscounts.ToastErrorTreatmentRequired'),
                otherwise: (schemaItem: Yup.ArraySchema<Yup.AnySchema>) => schemaItem.optional(),
            }),
        customerType: Yup.string().oneOf(['ALL', 'NEW', 'EXISTING']).required(),
        hasUsageLimit: Yup.boolean().default(false),
        maxUsesPerCustomer: Yup.number().when('hasUsageLimit', {
            is: true,
            then: (schemaItem: Yup.NumberSchema) => schemaItem.min(1).required(),
            otherwise: (schemaItem: Yup.NumberSchema) => schemaItem.optional().nullable(),
        }),
        allowOnlineBooking: Yup.boolean(),
        allowGiftCards: Yup.boolean(),
        allowPunchCards: Yup.boolean(),
    })
    .test(
        'channel-required',
        'BookingDiscounts.ToastErrorChannelRequired',
        (formValues: Record<string, unknown> | undefined, testContext: Yup.TestContext) => {
            if (!formValues) return true;
            const { allowOnlineBooking, allowGiftCards, allowPunchCards } = formValues;
            if (!allowOnlineBooking && !allowGiftCards && !allowPunchCards) {
                return testContext.createError({
                    path: 'allowOnlineBooking',
                    message: 'BookingDiscounts.ToastErrorChannelRequired',
                });
            }
            return true;
        },
    );
